// src/modules/access/accessConfig.ts
// Configuração e tipos para Centrais de Acesso

import { 
  Settings, 
  Building2, 
  Palette, 
  Calendar, 
  Users, 
  FileText, 
  DollarSign,
  MapPin,
  ShoppingBag,
  Anchor,
  Fish,
  Camera,
  Mail,
  Phone,
  Globe,
  Star,
  BarChart,
  MessageSquare,
  LucideIcon
} from 'lucide-react';

// ============================================
// TIPOS
// ============================================

export interface AccessButton {
  id: string;
  label: string;
  description: string;
  icon: string;  // Nome do ícone Lucide (para salvar no Firestore)
  path: string;
  color?: string;
  visible: boolean;
  order: number;
}

export interface AccessHubConfig {
  buttons: AccessButton[];
  customized: boolean;
  lastModified?: Date;
}

// Mapeamento de string para componente de ícone
export const iconMap: Record<string, LucideIcon> = {
  Settings,
  Building2,
  Palette,
  Calendar,
  Users,
  FileText,
  DollarSign,
  MapPin,
  ShoppingBag,
  Anchor,
  Fish,
  Camera,
  Mail,
  Phone,
  Globe,
  Star,
  BarChart,
  MessageSquare,
};

// ============================================
// CONFIGURAÇÃO ADMIN (Plataforma)
// ============================================

export const adminAccessButtons: AccessButton[] = [
  {
    id: 'admin-panel',
    label: 'Entrar na Rede',
    description: 'Acesso para usuários e empresas',
    icon: 'Users',
    path: '/login',
    color: 'brand-gold',
    visible: true,
    order: 1,
  },
  {
    id: 'construtor',
    label: 'Construtor de Sites',
    description: 'Criar e editar sites de pousadas',
    icon: 'Palette',
    path: '/construtor',
    color: 'brand-orange',
    visible: true,
    order: 2,
  },
  {
    id: 'tenant-login',
    label: 'Login Pousada',
    description: 'Acesso ao painel do estabelecimento',
    icon: 'Building2',
    path: '/login',
    color: 'blue-500',
    visible: true,
    order: 3,
  },
  {
    id: 'cadastro',
    label: 'Cadastrar Empresa',
    description: 'Registrar nova pousada ou guia',
    icon: 'Fish',
    path: '/cadastrar-empresa',
    color: 'green-500',
    visible: true,
    order: 4,
  },
];

// ============================================
// TEMPLATES POR TIPO DE CLIENTE
// ============================================

export const tenantTypeTemplates: Record<string, AccessButton[]> = {
  pousada: [
    {
      id: 'painel',
      label: 'Meu Painel',
      description: 'Gerenciar sua pousada',
      icon: 'Building2',
      path: '/admin/dashboard',
      visible: true,
      order: 1,
    },
    {
      id: 'reservas',
      label: 'Reservas',
      description: 'Ver e gerenciar reservas',
      icon: 'Calendar',
      path: '/admin/reservas',
      visible: true,
      order: 2,
    },
    {
      id: 'financeiro',
      label: 'Financeiro',
      description: 'Relatórios e pagamentos',
      icon: 'DollarSign',
      path: '/admin/financeiro',
      visible: true,
      order: 3,
    },
    {
      id: 'avaliacoes',
      label: 'Avaliações',
      description: 'Ver avaliações dos clientes',
      icon: 'Star',
      path: '/admin/avaliacoes',
      visible: true,
      order: 4,
    },
  ],

  guia: [
    {
      id: 'painel',
      label: 'Meu Painel',
      description: 'Gerenciar seu perfil de guia',
      icon: 'Anchor',
      path: '/admin/dashboard',
      visible: true,
      order: 1,
    },
    {
      id: 'agenda',
      label: 'Agenda',
      description: 'Gerenciar disponibilidade',
      icon: 'Calendar',
      path: '/admin/agenda',
      visible: true,
      order: 2,
    },
    {
      id: 'clientes',
      label: 'Clientes',
      description: 'Ver histórico de clientes',
      icon: 'Users',
      path: '/admin/clientes',
      visible: true,
      order: 3,
    },
  ],

  loja: [
    {
      id: 'painel',
      label: 'Meu Painel',
      description: 'Gerenciar sua loja',
      icon: 'ShoppingBag',
      path: '/admin/dashboard',
      visible: true,
      order: 1,
    },
    {
      id: 'produtos',
      label: 'Produtos',
      description: 'Gerenciar catálogo',
      icon: 'Fish',
      path: '/admin/produtos',
      visible: true,
      order: 2,
    },
    {
      id: 'pedidos',
      label: 'Pedidos',
      description: 'Ver pedidos',
      icon: 'FileText',
      path: '/admin/pedidos',
      visible: true,
      order: 3,
    },
  ],
};

// ============================================
// HELPERS
// ============================================

export function getIconComponent(iconName: string): LucideIcon {
  return iconMap[iconName] || Settings;
}

export function getDefaultButtonsForTenantType(type: string): AccessButton[] {
  return tenantTypeTemplates[type] || tenantTypeTemplates.pousada;
}

export function createEmptyButton(order: number): AccessButton {
  return {
    id: `new-${Date.now()}`,
    label: 'Novo Acesso',
    description: 'Descrição do acesso',
    icon: 'Settings',
    path: '/',
    visible: true,
    order,
  };
}
